<template>
	<section class="detail user-detail" :class="{ 'nested': nestedDetail }">
		<h1>User</h1>
		<div class="detail-wrapper" v-if="user">
			<header>
				<div class="header-row">
					<div class="user-header user-header">
						<h2 class="metric-stat margin-bottom-0">{{ user.name }}</h2>
						<h2 v-if="user.team_uuid" class="account-type"><router-link class="link-button" :to="`/team/${user.team_uuid}`">{{ user.user_type }}: {{ user.team_name }}</router-link></h2>
						<h2 v-else class="label account-type">{{ user.user_type }}</h2>
					</div>
					<nav>
						<nav class="header-nav-item toggle-dropdown filter" :class="{ 'active': showFilterDropdown }" @click="showFilterDropdown = !showFilterDropdown">
							<span class="toggle">
								<span class="toggle-label">Actions</span> <i class="ion-chevron-down icon icon-down"></i>
							</span>

							<div class="dropdown" v-if="showFilterDropdown">
								<a v-if="posthogUrl"><a :href="posthogUrl" target="_blank">Posthog</a></a>
								<a><router-link :to="'/users/' + activeId.toString() + '/user-activity'">User activity</router-link></a>
								<a><router-link :to="'/users/' + activeId.toString() + '/transfer'">Transfer</router-link></a>
								<a><router-link :to="'/users/' + activeId.toString() + '/cached-data'">Cached data</router-link></a>
								<a><router-link :to="'/users/' + activeId.toString() + '/expire-token'">Expire token</router-link></a>
								<a><router-link :to="{path: `/users/${activeId.toString()}/instrumentation-info`, query: { uuid: users[activeId] && users[activeId].user_uuid }}" >Instrumentation</router-link></a>
								<a v-if="user.apple_correlation_id"><router-link :to="'/users/' + activeId.toString() + '/apple-history'">Apple Subscription History</router-link></a>
								<a><router-link :to="'/users/' + activeId.toString() + '/migrations'">Migrations</router-link></a>
								<a><a @click="openModal('login')">Login as {{ user.name ?? user.email }}</a></a>
								<template v-if="user.deleted_date">
									<a><a @click="openModal('undelete')">Undelete</a></a>
									<a><a @click="openModal('hardDeleteNow')" class="hard-delete">Hard delete now<div>Permanent, no email</div></a></a>
								</template>
								<a v-else><a @click="openModal('delete')" class="hard-delete">Hard delete<div>Sends email</div></a></a>
							</div>
						</nav>
					</nav>
				</div>
				<i class="detail-header-icon hide detail-hide" @click="closeView">✕</i>
			</header>

			<section class="blocks">
				<div class="block basic-info-block">
					<span class="title">Basic info</span>
					<ul>
						<li class="detail-row-notes">
							<div class="label-row">
								<span class="label">Default Email</span>
								<a title="Search on Stripe" v-if="user.email" class="icon-link" :href="'https://dashboard.stripe.com/search?query='+ encodeURIComponent(user.email)" target="_blank"><i class="ion-android-open"></i></a>
							</div>
							<copyable-value :value="user.email">
								{{ user.email }}<span v-if="!!user.when_validated" title="Verified"><i class="fa fa-check verified"></i></span>
							</copyable-value>
						</li>
						<li class="detail-row-notes">
							<div class="label-row">
								<span class="label">Other Emails</span>
							</div>
							<ul class="other-emails-list">
								<li v-for="email in emails" :key="email.email">
									<copyable-value :value="email.email">
										{{ email.email }}<span v-if="!!email.when_validated" title="Verified"><i class="fa fa-check verified"></i></span>
									</copyable-value>
									<span v-if="email.deleted" class="deleted-date">Deleted on {{ formatDate(email.deleted) }}</span>
								</li>
							</ul>
						</li>
						<li class="detail-row-notes">
							<div class="label-row">
								<span class="label">Registered</span>
							</div>
							<div>{{ formatDate(user.created) }}</div>
						</li>
						<li v-if="user.deleted_date" class="detail-row-notes">
							<div class="label-row">
								<span class="label">Deleted</span>
							</div>
							<div>{{ formatDate(user.deleted_date) }}</div>
						</li>
						<li class="detail-row-notes">
							<div class="label-row">
								<span class="label">ID</span>
								<a title="Search on Stripe" v-if="activeId" class="icon-link" :href="'https://dashboard.stripe.com/search?query='+ activeId" target="_blank"><i class="ion-android-open"></i></a>
							</div>
							<copyable-value :value="activeId">{{ activeId }}</copyable-value>
						</li>
						<li v-if="user.stripe_id" class="detail-row-notes">
							<div class="label-row">
								<span class="label">Stripe ID</span>
								<a title="View on Stripe" v-if="user.stripe_id" class="icon-link" :href="'https://dashboard.stripe.com/customers/'+ user.stripe_id" target="_blank"><i class="ion-android-open"></i></a>
							</div>
							<copyable-value :value="user.stripe_id">{{ user.stripe_id }}</copyable-value>
						</li>
						<li v-if="user.apple_correlation_id" class="detail-row-notes">
							<div class="label-row">
								<span class="label">Apple Correlation Id</span>
								<router-link class="icon-link" title="View Apple Subscription History" :to="'/users/' + activeId.toString() + '/apple-history'"><i class="ion-android-arrow-forward"></i></router-link>
							</div>
							<copyable-value :value="user.apple_correlation_id">{{ user.apple_correlation_id }}</copyable-value>
						</li>
						<li class="detail-row-notes">
							<div class="label-row">
								<span class="label">User Uuid</span>
							</div>
							<copyable-value :value="user.user_uuid">{{ user.user_uuid }}</copyable-value>
						</li>
						<li class="detail-row-notes" v-if="posthogDistinctId">
							<div class="label-row">
								<span class="label">Posthog Distinct Id</span>
								<a v-if="posthogUrl" class="icon-link" title="View in Posthog" :href="posthogUrl" target="_blank"><i class="ion-android-open"></i></a>
							</div>
							<copyable-value :value="posthogDistinctId">{{ posthogDistinctId }}</copyable-value>
						</li>
						<li class="detail-row-notes">
							<div class="label-row">
								<span class="label">Collection</span>
							</div>
							<div>{{ user.nosql_db_collection_id }}</div>
						</li>
					</ul>

					<span class="title installation-info-title">Installation info</span>
					<transition name="fade">
						<div v-if="loadingInstallationInfo" class="loading">Loading...</div>
						<div v-else-if="!installationInfo" class="server-data-error">There is an issue loading installation info. Please try again.</div>
						<ul v-else>
							<li class="detail-row-notes">
								<div class="item-row">
									<div class="label">Install uuid</div>
								</div>
								{{ installationInfo.client_uuid }}
							</li>
							<li class="detail-row-notes">
								<div class="item-row">
									<div class="label">Browser info</div>
								</div>
								{{ installationInfo.user_agent }}
							</li>
							<li class="detail-row-notes">
								<div class="item-row">
									<div class="label">Created</div>
								</div>
								{{ formatDate(installationInfo.client_created) }}
							</li>
							<li class="detail-row-notes">
								<div class="item-row">
									<div class="label">Link Created</div>
								</div>
								{{ formatDate(installationInfo.link_created) }}
							</li>
						</ul>
					</transition>
				</div>

				<div class="block">
					<span class="title">Feature info</span>
					<transition name="fade">
						<div v-if="loadingFeatureInfo" class="loading">Loading...</div>
						<div v-else-if="!loadingFeatureInfo && !featureSets && !enabledFeatures" class="server-data-error">There is a problem loading user feature info. Please try again.</div>
						<ul v-else>
							<li class="detail-row-notes">
								<span class="label">Feature sets</span>
								<table>
									<tr class="heading">
										<th>Name</th>
										<th>Created</th>
										<th>Expiry</th>
										<th>Revoked</th>
									</tr>

									<tr v-for="featureSet in featureSets" :key="featureSet.featureset_name" class="no-hover">
										<td>{{ featureSet.featureset_name }}</td>
										<td>{{ formatDate(featureSet.created) }}</td>
										<td>{{ formatDate(featureSet.expiry_date) }}</td>
										<td>{{ formatDate(featureSet.revoked_date) }}</td>
									</tr>
								</table>
							</li>

							<li class="detail-row-notes">
								<span class="label">Enabled features</span>
								<ul class="features-list">
									<li v-for="(feature, key) in enabledFeatures" :key="key" class="feature-alias">{{ feature.feature_alias }}</li>
								</ul>
							</li>
						</ul>
					</transition>
				</div>
				<div class="block" v-if="userExperiments.length">
					<span class="title">Experiments</span>
					<table>
						<tr class="heading">
							<th>Experiment</th>
							<th>Variant</th>
							<th>Active</th>
						</tr>
						<router-link class="experiment" tag="tr" v-for="userExperiment in userExperiments" :key="userExperiment.id" :to="`/experiments/${userExperiment.variant.experiment.id}`">
							<td>{{ userExperiment.variant.experiment.name }}</td>
							<td>{{ userExperiment.variant.name }}</td>
							<td>
								<span v-if="userExperiment.variant.experiment.active" title="Live" class="live"><i class="fa fa-check"></i></span>
								<span v-else><i class="fa fa-times"></i></span>
							</td>
						</router-link>
					</table>
				</div>
			</section>
		</div>

		<delete-user-modal :user-id="user.id" :is-open="activeModal === 'delete'"  @close="closeModal"  />
		<undelete-user-modal :user-id="user.id" :is-open="activeModal === 'undelete'"  @close="closeModal"  />
		<hard-delete-now-modal :user-id="user.id" :is-open="activeModal === 'hardDeleteNow'"  @close="closeModal" @purged="closeView"  />
		<login-as-user-modal :user-id="user.id" :is-open="activeModal === 'login'" @close="closeModal"  />

		<transition name="detail-nested">
			<router-view @closeNested="closeNested"></router-view>
		</transition>
	</section>
</template>



<script>
import Vue from 'vue'
import { mixin as VueFocusMixin } from 'vue-focus'
import detailsMixins from '../../mixins/detailsMixins'
import util from "@/util";
import DeleteUserModal from '../modals/DeleteUserModal.vue';
import UndeleteUserModal from '../modals/UndeleteUserModal.vue';
import HardDeleteNowModal from '../modals/HardDeleteNowModal.vue';
import LoginAsUserModal from  '../modals/LoginAsUserModal.vue';
import CopyableValue from '../shared/CopyableValue.vue';

export default {
	name: 'user-detail',
	store: ['users'],
	components: {
		DeleteUserModal,
		UndeleteUserModal,
		HardDeleteNowModal,
		LoginAsUserModal,
		CopyableValue
	},
	data() {
		return {
			activeModal: null,
			showFilterDropdown: false,
			emails:[],
			loadingFeatureInfo: false,
			featureSets: null,
			enabledFeatures: null,
			loadingInstallationInfo: false,
			installationInfo: null,
			posthogDistinctId: null,
			userExperiments: [],
		}
	},

	computed: {
		user() {
			return this.users[this.activeId]
		},
		posthogUrl() {
			if (!this.posthogDistinctId) return null
			return `${process.env.VUE_APP_POSTHOG_URI}person/${this.posthogDistinctId}`
		}
	},

	mixins: [VueFocusMixin, detailsMixins],

	watch: {
		'$route' () {
			this.checkNested()
		},
		'user.user_uuid': {
			async handler(uuid) {
				this.posthogDistinctId = await util.getDistinctId(uuid)
			},
			immediate: true
		},
		user: {
			handler(val, oldVal) {
				if (val && !oldVal) {
					this.getFeatureInfo()
					this.getInstallationInfo()
					this.getUserEnrolledExperiments()
				}
			},
			immediate: true
		},
	},

	mounted() {
		this.checkNested()
		this.doSearch()
	},

	methods: {
		// Escape must not close the detail view (and tear down a modal mid-request) while a modal is open
		handleKeyup(e) {
			if (!this.activeModal) detailsMixins.methods.handleKeyup.call(this, e);
		},
		openModal(modalType) {
			this.activeModal = modalType;
		},
		closeModal() {
			this.activeModal = null;
		},
		checkNested() {
			this.nestedDetail = this.$route.meta.viewType === 'nestedDetail'
			this.activeId = this.$route.params.id
			this.loadEmails(this.activeId)
		},
		loadEmails(id) {
			this.$http.get(`/users/${id}/emails`)
				.then(response => {
					this.emails = response.data.emails.filter(email => !email.is_default)
				})
				.catch(error => {
					console.log(error)
				})
		},
		doSearch() {
			let searchParams = {
				params: {
					input: this.activeId
				}
			}
			let requestParams = { timeout: 40000 }
			this.$http.get('/users/search', searchParams, requestParams)
				.then((response) => {
					this.foundUsers = response.data.users || []
					this.foundUsers.forEach((user) => {
						Vue.set(this.users, user.id, user)
					})
				})
				.catch(function(error) {
					console.log(error)
				})
		},
		closeNested() {
			window.location.href = '/#/users/' + this.activeId
		},

		getFeatureInfo() {
			this.loadingFeatureInfo = true
			if (!this.user || !this.user.user_uuid) return;
			const url = `/admin/${this.user.user_uuid}/feature-sets`
			this.$http.get(url)
				.then(res => {
					this.enabledFeatures = res.data.featuresEnabled
					this.featureSets = res.data.featureSets
				})
				.catch(error => console.log(`Error in GET ${ url }: ${ error }`))
				.finally(() => this.loadingFeatureInfo = false)
		},

		getInstallationInfo() {
			this.loadingInstallationInfo = true
			if (!this.user || !this.user.user_uuid) return;
			const url = `/admin/${this.user.user_uuid}/installation-info`
			this.$http.get(url)
				.then(res => this.installationInfo = res.data[0])
				.catch(error => console.log(`Error in GET ${ url }: ${ error }`))
				.finally(() => this.loadingInstallationInfo = false)
		},

		async getUserEnrolledExperiments() {
			try {
				const response = await this.$http.get(`/admin/experiment_manual_enrollments/?filter=userId%3D${this.user.user_uuid}`)
				this.userExperiments = response.data
			} catch (error) {
				console.error(error)
				this.error = error
			}
		},
	}
}
</script>



<style scoped>
	.basic-info-block { flex-basis: 20%; }

		.detail-row-notes:last-child { margin-bottom: 0; }

	.block .title.installation-info-title { margin-top: 2em; }

	.filter .dropdown a.hard-delete {
		color: tomato;
	}
	.filter .dropdown a.hard-delete > div {
		opacity: 0.7;
		font-size: 70%;
	}

	.deleted-date { font-size: 0.8rem; font-weight: 700; color: red; opacity: 0.5; }
	.account-type { padding-left: 10px; }

	.features-list {  -webkit-columns: 2; -moz-columns: 2; columns: 2; }
		.feature-alias { margin-top: 10px; margin-bottom: 0; }

	.experiment { cursor: pointer; }

	.verified { color: var(--color-accent); margin-left: 4px; }

	.header-row { display: flex; flex-wrap: wrap; align-items: center; align-content: space-between; padding-bottom: 25px; }
		.user-header { flex: 1; }
	.margin-bottom-0 { margin-bottom: 0; }

	.label-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
}

	.label-row .label {
		color: #6b7280;
		font-size: 0.8em;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.025em;
	}

	.icon-link {
		display: inline-flex;
		align-items: center;
		padding: 4px;
		margin-bottom: 6px;
		color: #9ca3af;
		transition: color 0.15s ease;
	}
	.icon-link:hover {
		color: var(--color-accent);
	}
</style>
